import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Instagram, Clock, Send, MessageCircle, ArrowRight, CheckCircle2, Phone,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { BRAND, SERVICE_OPTIONS } from "../data/site";

const initialForm = {
  name: "", business_name: "", phone: "", email: "", service: "", message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) {
      toast.error("Please fill in name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, { ...form, source: "contact_form" });
      toast.success("Thank you! We'll reach out within 24 hours.");
      setSubmitted(true);
      setForm(initialForm);
    } catch (err) {
      toast.error("Failed to send. Please try again or message us on WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const whatsappHref = `https://wa.me/${BRAND.whatsappNumber}?text=${encodeURIComponent(
    "Hi Zyvora Digital, I'd like to discuss a project."
  )}`;

  return (
    <div className="bg-[#0A0A0A] text-white pt-28 pb-24">
      <section className="relative py-16 lg:py-20 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute -top-24 right-0 h-[420px] w-[420px] rounded-full bg-[#F5D67B]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <p className="text-sm font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-3">Get In Touch</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] max-w-4xl">
            Let's grow your business <span className="text-gradient-brand">online</span>
          </h1>
          <p className="mt-6 text-lg text-gray-300 max-w-2xl">
            Tell us about your business and goals. We'll respond within 24 hours with a clear, no-obligation plan.
          </p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
          {/* Contact info */}
          <div className="lg:col-span-5 space-y-5">
            <div className="rounded-2xl border border-white/10 bg-[#121212] p-7">
              <h3 className="font-display text-xl font-semibold mb-5">Contact Information</h3>
              <ul className="space-y-5">
                <li className="flex items-start gap-4" data-testid="contact-phones">
                  <div className="h-11 w-11 rounded-xl bg-gradient-brand-soft border border-white/10 flex items-center justify-center shrink-0">
                    <Phone className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Phone</p>
                    <ul className="space-y-1">
                      {BRAND.phones.map((p, i) => (
                        <li key={i} className="flex items-center gap-2 text-white">
                          <a
                            href={`tel:${p.tel}`}
                            data-testid={`contact-phone-${i}`}
                            className="font-medium hover:text-[#F5D67B] transition"
                          >
                            {p.value}
                          </a>
                          <span className="text-[10px] uppercase tracking-widest text-gray-500">{p.label}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-gradient-brand-soft border border-white/10 flex items-center justify-center shrink-0">
                    <Mail className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Email</p>
                    <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="text-white font-medium hover:text-[#F5D67B] transition break-all">
                      {BRAND.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-gradient-brand-soft border border-white/10 flex items-center justify-center shrink-0">
                    <Instagram className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Instagram</p>
                    <a href={BRAND.instagramUrl} target="_blank" rel="noreferrer" data-testid="contact-instagram" className="text-white font-medium hover:text-[#F5D67B] transition">
                      {BRAND.instagram}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-gradient-brand-soft border border-white/10 flex items-center justify-center shrink-0">
                    <Clock className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Business Hours</p>
                    <p className="text-white font-medium">{BRAND.businessHours}</p>
                    <p className="text-xs text-gray-400 mt-1">Response within 24 hours</p>
                  </div>
                </li>
              </ul>
            </div>

            <a
              href={whatsappHref}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-button"
              className="block rounded-2xl bg-green-500 hover:bg-green-600 transition p-6 text-white"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <MessageCircle className="h-7 w-7" />
                  <div>
                    <p className="font-display text-lg font-bold">WhatsApp Us</p>
                    <p className="text-sm text-green-50">Fastest way to get a reply</p>
                  </div>
                </div>
                <ArrowRight className="h-5 w-5" />
              </div>
            </a>

            <div className="rounded-2xl border border-white/10 bg-[#121212] p-7">
              <h4 className="font-display text-base font-semibold mb-3">What happens next?</h4>
              <ul className="space-y-3 text-sm text-gray-300">
                {["We review your enquiry within 24 hours", "We schedule a free 20-minute consultation", "You receive a clear quote & timeline"].map((t, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="rounded-3xl border border-white/10 bg-[#121212] p-7 lg:p-10"
            >
              <h2 className="font-display text-2xl md:text-3xl font-bold mb-2">Send an Enquiry</h2>
              <p className="text-sm text-gray-400 mb-7">All fields marked with * are required.</p>

              {submitted && (
                <div data-testid="contact-success-banner" className="mb-6 rounded-xl border border-green-500/30 bg-green-500/10 p-4 text-sm text-green-200 flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5" />
                  Thanks! Your enquiry was received. We'll be in touch within 24 hours.
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-gray-200 text-sm mb-1.5 block">Name *</Label>
                  <Input id="name" data-testid="form-name-input" value={form.name}
                    onChange={(e) => update("name", e.target.value)} required
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl" />
                </div>
                <div>
                  <Label htmlFor="business" className="text-gray-200 text-sm mb-1.5 block">Business Name</Label>
                  <Input id="business" data-testid="form-business-input" value={form.business_name}
                    onChange={(e) => update("business_name", e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl" />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-gray-200 text-sm mb-1.5 block">Phone Number *</Label>
                  <Input id="phone" type="tel" data-testid="form-phone-input" value={form.phone}
                    onChange={(e) => update("phone", e.target.value)} required
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl" />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-200 text-sm mb-1.5 block">Email *</Label>
                  <Input id="email" type="email" data-testid="form-email-input" value={form.email}
                    onChange={(e) => update("email", e.target.value)} required
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 h-12 rounded-xl" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-gray-200 text-sm mb-1.5 block">Service Required</Label>
                  <Select value={form.service} onValueChange={(v) => update("service", v)}>
                    <SelectTrigger data-testid="form-service-select" className="bg-white/5 border-white/10 text-white h-12 rounded-xl">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121212] border-white/10 text-white">
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`form-service-opt-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="message" className="text-gray-200 text-sm mb-1.5 block">Message</Label>
                  <Textarea id="message" data-testid="form-message-input" rows={5} value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Tell us a bit about your business and what you're looking for…"
                    className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 rounded-xl" />
                </div>
              </div>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Button
                  type="submit"
                  data-testid="form-submit-button"
                  disabled={loading}
                  className="bg-gradient-brand text-[#0A0A0A] rounded-full h-12 px-7 font-semibold"
                >
                  {loading ? "Sending…" : "Send Inquiry"}
                  <Send className="ml-2 h-4 w-4" />
                </Button>
                <a href={whatsappHref} target="_blank" rel="noreferrer" data-testid="form-whatsapp-secondary">
                  <Button type="button" variant="outline" className="border-white/15 bg-white/5 text-white hover:bg-white/10 rounded-full h-12 px-6 font-semibold">
                    Request Quote on WhatsApp
                  </Button>
                </a>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
